/**
 * Ядро боя (E1): одна волна, фиксированный шаг 30 Гц, целые числа, сидированный ГПСЧ.
 *
 * Ввод — только команды игрока с тиками ({@link TimedCommand}); всё остальное выводится из сида, баланса и
 * состояния забежки. Поэтому клиент и сервер, прогнав один журнал, получают один {@link WaveResult}.
 *
 * Порядок тика: команды → появления → способности боссов → зомби (ход, удар) → штаб (проигрыш, реген) →
 * герои и пушка → снаряды → итог волны → визуальное (эффекты, монеты, трупы).
 */
import type {
  AttackDefense,
  AttackState,
  BalanceData,
  BattleEvent,
  BattleSnapshot,
  CoinDropState,
  EffectKind,
  EffectState,
  FieldGeometry,
  HeroId,
  HqLook,
  ParcelOpened,
  PlayerCommand,
  ProjectileKind,
  RunState,
  StatModifier,
  TimedCommand,
  Vec2,
  WavePhase,
  WaveResult,
  ZombieKind,
  ZombieState,
} from "@newfungame/contracts";
import { PERMILLE, TICK_RATE } from "@newfungame/contracts";
import { StateHasher } from "./hash";
import {
  BOSS_RADIUS,
  HQ_RADIUS,
  PLATFORM_RADIUS,
  ZOMBIE_RADIUS,
  createAttackLayout,
  createLayout,
  type AttackLayout,
  type Layout,
} from "./layout";
import { attackPlan, mapDef } from "./attack";
import { dist2, divTrunc, facingOf, isqrt, mulPermille } from "./math";
import { parcelPlan } from "./parcels";
import { createRng, type Rng } from "./rng";
import { collectRunModifiers, globalStats, heroAttack, hqStats, type HqStats } from "./stats";
import { HERO_BEHAVIORS } from "../heroes/registry";
import { platformUnlockWaves } from "../heroes/roster";
import type { HeroBehavior, HeroEntity, Shooter, SimApi, ZombieEntity } from "./types";
import { waveBaseReward, waveReward } from "../economy/income";
import { slotCount } from "../beta";
import { generateWave, levelOf, rollbackWave, runHpPermille, scaledHp, type SpawnEntry, type WavePlan } from "./waves";

/** Сколько тиков держим убитого зомби с анимацией `die`. */
export const DIE_TICKS = 15;
/** Длина анимации выстрела героя. */
const SHOOT_ANIM_TICKS = 6;
/** Полёт монеты до счётчика. */
const COIN_FLIGHT_TICKS = 20;
/** Бой атаки: защитник ближе этого расстояния до стрелка отвлекает его от базы. */
export const ATTACK_THREAT_RANGE = 2600;
/** Предохранитель: волна дольше 20 минут — проигрыш (не должно случаться при любом балансе с уроном зомби > 0). */
export const MAX_WAVE_TICKS = 20 * 60 * TICK_RATE;

/** Поведение по умолчанию — автострельба атакой героя по ближайшему к штабу зомби в радиусе. */
export const autoAttack: HeroBehavior = {
  tick(hero, sim) {
    shooterTick(hero, sim);
  },
};

/** Стандартная автострельба стрелка: перезарядка, цель, выстрел. Годится для героев E3 и пушки. */
export function shooterTick(s: Shooter, sim: SimApi): void {
  if (s.cooldownLeft > 0) s.cooldownLeft--;
  if (s.cooldownLeft > 0) return;
  const target = sim.findTarget(s.x, s.y, s.attack.range);
  if (target) sim.fire(s, target);
  else s.targetId = null;
}

/** Поведения героев по умолчанию — все пять героев с навыками (E3, `engine/src/heroes/`); `hooks.heroes` — поверх. */
export const DEFAULT_HERO_BEHAVIORS: Partial<Record<HeroId, HeroBehavior>> = { ...HERO_BEHAVIORS };

/** Точки расширения боя. */
export interface BattleHooks {
  /** Поведения героев поверх {@link DEFAULT_HERO_BEHAVIORS}. Герой без поведения на поле не выходит. */
  heroes?: Partial<Record<HeroId, HeroBehavior>>;
  /** Дополнительные модификаторы (сверх Tech и усилений забежки). */
  modifiers?: readonly StatModifier[];
  /**
   * Штаб пал на тике `tick`: вернуть прочность в промилле от максимума, и бой идёт дальше; 0 — поражение. Только замер
   * «продолжения боя» (`sim-bot/revive.ts`, ISSUE-81): сервер и клиент хук не задают, журналы переигрываются как раньше.
   */
  onHqDown?: (tick: number) => number;
}

/** Вход боя. */
export interface BattleInit {
  balance: BalanceData;
  /** Состояние забежки на старте волны: номер волны, герои, Tech, усиления, прочность штаба. */
  run: RunState;
  /** Сид волны от сервера (`PlayerState.next.seed`). */
  seed: number;
  /** Подтверждённые монеты игрока — только для `wallet` в снимке. */
  walletCoins?: number;
  field?: FieldGeometry;
  hooks?: BattleHooks;
  /**
   * Разрешены ли автонавыки. Не задано — разрешены: старые журналы и тесты переигрываются как раньше. Сервер и клиент
   * передают VIP игрока (MADR 0003).
   */
  autoSkillsAllowed?: boolean;
  /**
   * Режим «Карта» (только бета): бой атаки участка. База-цель в центре, `run` — забежка атакующего (его герои стоят по
   * краю), `defense` — состав защиты, который зафиксировал сервер. Нет — обычный бой, ничего не меняется.
   */
  attack?: AttackDefense;
}

/** Герой-защитник под ИИ (защита чужого участка). */
interface Defender extends Shooter {
  owner: HeroId;
  id: number;
  heroId: HeroId;
  slot: number;
  level: number;
  stars: number;
  anim: "idle" | "shoot";
  animFrame: number;
  targetSlot: number | null;
}

/** Выбор усиления после волны: ядро только запоминает, проверяет и применяет E4. */
export interface BoostChoice {
  index: number;
  boostId: string;
}

/** Бой одной волны. */
export interface Battle {
  /** Шаг на тик (1/30 с). Возвращает события тика. После конца волны двигает только визуальное. */
  step(): readonly BattleEvent[];
  /** Снимок для рендера и HUD (новый объект, события последнего шага). */
  snapshot(): BattleSnapshot;
  /**
   * Команда игрока: применится в начале следующего шага (после конца волны — сразу). Записывается в
   * {@link Battle.commands} с тиком применения — это и есть журнал для сервера.
   */
  command(cmd: PlayerCommand): void;
  /** Запланировать команду из журнала на её тик (повтор волны). */
  schedule(cmd: TimedCommand): void;
  /** Журнал команд с тиками (для `WaveLog.commands`). */
  commands(): TimedCommand[];
  /** VIP куплен посреди волны: автонавыки доступны с этого тика. Сервер считает так всю волну, команд до этого нет. */
  allowAutoSkills(): void;
  /** Автонавыки разрешены в этой волне (`WaveLog.vip`). */
  autoSkillsAllowed(): boolean;
  /** Тик волны (сколько тиков симулировано). */
  tick(): number;
  isOver(): boolean;
  /** Итог волны; `null`, пока волна идёт. */
  result(): WaveResult | null;
  /** Хэш текущего игрового состояния (визуальное не входит). */
  stateHash(): string;
  /** Выбор усиления, если был (команда `chooseBoost` после `cleared`). */
  boostChoice(): BoostChoice | null;
  /** План волны (для отладки, симулятора баланса E6 и тестов). */
  readonly plan: WavePlan;
}

interface Projectile {
  id: number;
  kind: ProjectileKind;
  owner: HeroId | "hq";
  x: number;
  y: number;
  fromX: number;
  fromY: number;
  tx: number;
  ty: number;
  targetId: number;
  speed: number;
  damage: number;
  crit: boolean;
  splash: number;
}

interface Corpse {
  state: ZombieState;
  frame: number;
}

const ZOMBIE_KIND_CODE: Record<ZombieKind | "boss", number> = { walker: 1, runner: 2, brute: 3, shield: 4, boss: 5 };
const PHASE_CODE: Record<WavePhase, number> = { intro: 0, fight: 1, cleared: 2, lost: 3 };

export function createBattle(init: BattleInit): Battle {
  const { balance, run } = init;
  const level = levelOf(balance, run.wave);
  // бета: сетка 4×4 слотов вокруг штаба, копии героев, слоты открывает ветка Tech `base`
  const atk = init.attack ?? null;
  const beta = level !== null && balance.beta !== undefined;
  if (atk && !beta) throw new RangeError("attack battle needs the beta balance");
  const layout: Layout = atk ? createAttackLayout(init.field) : createLayout(init.field, beta);
  const rng: Rng = createRng(init.seed);
  const wave = run.wave;
  const plan: WavePlan = atk
    ? attackPlan(balance, atk.kind === "zombies" ? atk : null, rng, layout as AttackLayout, wave)
    : { ...generateWave(balance, wave, rng, layout.field), hpPermille: runHpPermille(balance, run) };
  const amap = atk ? mapDef(balance).attack : null;
  const behaviors = { ...DEFAULT_HERO_BEHAVIORS, ...init.hooks?.heroes };
  const mods = [...collectRunModifiers(balance, run), ...(init.hooks?.modifiers ?? [])];
  const hqs: HqStats = hqStats(balance, mods);
  const glob = globalStats(mods);
  const autoFromWave = wave >= balance.waves.autoSkillsFromWave;
  let autoUnlocked = autoFromWave && (init.autoSkillsAllowed ?? true);

  // --- состояние
  let t = 0;
  let frame = 0;
  let phase: WavePhase = "intro";
  let nextId = 1;
  let nextVisualId = 1;
  let spawnIdx = 0;
  let total = plan.spawns.length;
  let spawned = 0;
  let kills = 0;
  let coinsEarned = 0;
  let bossKilled = false;
  let bossEntityId: number | null = null;
  let autoEnabled = run.autoSkills && autoUnlocked;
  let hqHp = Math.min(run.hqHp, hqs.hpMax);
  let result: WaveResult | null = null;
  let boost: BoostChoice | null = null;
  let walletCollected = 0;
  let events: BattleEvent[] = [];

  let zombies: ZombieEntity[] = [];
  let projectiles: Projectile[] = [];
  let effects: EffectState[] = [];
  let coinDrops: CoinDropState[] = [];
  let corpses: Corpse[] = [];
  const pending: TimedCommand[] = [];
  const log: TimedCommand[] = [];
  const parcels = atk ? [] : parcelPlan(balance, wave, init.seed);
  const parcelsOpened = new Set<number>();
  let lastOpened: ParcelOpened | null = null;

  const cannon: Shooter = {
    owner: "hq",
    x: layout.hq.x,
    y: layout.hq.y,
    attack: hqs.cannon,
    cooldownLeft: 0,
    targetId: null,
    facing: "n",
    firedFrame: -1,
  };

  // платформа открывается вместе со своим героем, шестая — с начала (запас, E3: heroes/roster.ts)
  const betaSlots = beta ? slotCount(balance, run.tech) : 0;
  const platformUnlock = beta
    ? layout.platforms.map((_, i) => (i < betaSlots ? 1 : 999_999))
    : platformUnlockWaves(balance);
  // в составе только открытые герои, значит игрок уже доходил до их волны: откат их не снимает (ISSUE-60)
  const openedWave = balance.waves.keepOpenedHeroes
    ? run.heroes.reduce((w, rh) => Math.max(w, balance.heroes.find((h) => h.id === rh.heroId)?.unlockWave ?? 0), wave)
    : wave;
  const heroes: HeroEntity[] = [];
  for (const rh of [...run.heroes].sort((a, b) => a.slot - b.slot)) {
    if (!behaviors[rh.heroId] || heroes.some((h) => h.slot === rh.slot || (!beta && h.heroId === rh.heroId))) continue;
    const attack = heroAttack(balance, rh.heroId, rh.level, rh.stars, mods);
    if (!attack) continue;
    const def = balance.heroes.find((h) => h.id === rh.heroId)!;
    // герой ещё не открыт или платформа закрыта — на поле не выходит
    if (def.unlockWave > openedWave || (platformUnlock[rh.slot] ?? Infinity) > openedWave) continue;
    const skill = balance.skills.find((s) => s.id === def.skillId);
    const pos = layout.platforms[rh.slot]!;
    heroes.push({
      owner: rh.heroId,
      id: nextId++,
      heroId: rh.heroId,
      slot: rh.slot,
      level: rh.level,
      stars: rh.stars,
      x: pos.x,
      y: pos.y,
      attack,
      cooldownLeft: 0,
      targetId: null,
      facing: "n",
      firedFrame: -1,
      anim: "idle",
      animFrame: 0,
      skill: {
        skillId: def.skillId,
        cooldownTotal: skill?.cooldownTicks ?? 0,
        cooldownLeft: skill?.cooldownTicks ?? 0,
        durationTicks: skill?.durationTicks ?? 0,
        activeLeft: 0,
      },
      data: {},
    });
  }

  // --- бой атаки: база-цель, прочность платформ атакующего, защитники-герои
  const platformHpMax = amap ? Math.max(1, mulPermille(hqs.hpMax, amap.platformHpPermille)) : 0;
  const platformHp = new Map<number, number>();
  for (const h of heroes) platformHp.set(h.slot, platformHpMax);
  const zombieTarget = new Map<number, number>();
  let baseEnt: ZombieEntity | null = null;
  const defenders: Defender[] = [];
  if (atk) {
    baseEnt = {
      id: nextId++,
      kind: "boss",
      bossId: "attack_base",
      x: layout.hq.x,
      y: layout.hq.y,
      hp: atk.baseHp,
      hpMax: atk.baseHp,
      shield: 0,
      speed: 0,
      speedPermille: PERMILLE,
      statuses: [],
      damage: 0,
      attackCooldownTicks: 1,
      attackLeft: 0,
      coins: 0,
      radius: HQ_RADIUS,
      mode: "attack",
      animFrame: 0,
      facing: "s",
      data: {},
      ability: null,
    };
    zombies.push(baseEnt);
    if (atk.kind === "heroes") {
      const dmods = collectRunModifiers(balance, { ...run, tech: atk.tech, boosts: [] });
      for (const dh of [...atk.heroes].sort((a, b) => a.slot - b.slot)) {
        const a = heroAttack(balance, dh.heroId, dh.level, dh.stars, dmods);
        const pos = (layout as AttackLayout).defenders[dh.slot];
        if (!a || !pos || defenders.some((d) => d.slot === dh.slot)) continue;
        defenders.push({
          owner: dh.heroId,
          id: nextId++,
          heroId: dh.heroId,
          slot: dh.slot,
          level: dh.level,
          stars: dh.stars,
          x: pos.x,
          y: pos.y,
          attack: a,
          cooldownLeft: a.cooldownTicks,
          targetId: null,
          facing: "n",
          firedFrame: -1,
          anim: "idle",
          animFrame: 0,
          targetSlot: null,
        });
      }
    }
  }

  // --- вспомогательное
  const emit = (e: BattleEvent): void => {
    events.push(e);
  };
  const effect = (kind: EffectKind, pos: Vec2, to: Vec2 | null, durationTicks: number, value: number): void => {
    effects.push({ id: nextVisualId++, kind, pos: { x: pos.x, y: pos.y }, to, startTick: frame, durationTicks, value });
  };
  const alive = (z: ZombieEntity): boolean => z.hp > 0;
  const hqDist2 = (z: ZombieEntity): number => dist2(z.x, z.y, layout.hq.x, layout.hq.y);

  function zombieState(z: ZombieEntity, dying: boolean): ZombieState {
    return {
      id: z.id,
      kind: z.kind,
      bossId: z.bossId,
      pos: { x: z.x, y: z.y },
      facing: z.facing,
      anim: dying ? "die" : z.mode,
      animTick: dying ? 0 : frame - z.animFrame,
      hp: z.hp,
      hpMax: z.hpMax,
      shield: z.shield,
      statuses: [...z.statuses],
    };
  }

  function spawnZombie(kind: ZombieKind | "boss", bossId: string | null, x: number, y: number): ZombieEntity | null {
    let z: ZombieEntity;
    if (kind === "boss") {
      const def = balance.bosses.find((b) => b.id === bossId);
      if (!def) return null;
      const hp = scaledHp(def.hp, plan.hpPermille);
      z = {
        id: nextId++,
        kind,
        bossId: def.id,
        x,
        y,
        hp,
        hpMax: hp,
        shield: 0,
        speed: def.speed,
        speedPermille: PERMILLE,
        statuses: [],
        damage: def.damage,
        attackCooldownTicks: def.attackCooldownTicks,
        attackLeft: 0,
        coins: def.coins,
        radius: BOSS_RADIUS,
        mode: "walk",
        animFrame: frame,
        facing: "s",
        data: {},
        ability: {
          kind: def.ability,
          cooldownTicks: def.abilityCooldownTicks,
          left: def.abilityCooldownTicks,
          params: def.params,
          chargeLeft: 0,
        },
      };
    } else {
      const def = balance.zombies.find((d) => d.kind === kind);
      if (!def) return null;
      const hp = scaledHp(def.hp, plan.hpPermille);
      z = {
        id: nextId++,
        kind,
        bossId: null,
        x,
        y,
        hp,
        hpMax: hp,
        shield: def.shieldHp > 0 ? scaledHp(def.shieldHp, plan.hpPermille) : 0,
        speed: def.speed,
        speedPermille: PERMILLE,
        statuses: [],
        damage: def.damage,
        attackCooldownTicks: def.attackCooldownTicks,
        attackLeft: 0,
        coins: def.coins,
        radius: ZOMBIE_RADIUS,
        mode: "walk",
        animFrame: frame,
        facing: "s",
        data: {},
        ability: null,
      };
    }
    z.facing = facingOf(layout.hq.x - x, layout.hq.y - y);
    zombies.push(z);
    spawned++;
    emit({ type: "zombieSpawned", id: z.id, kind: z.kind });
    if (z.kind === "boss" && z.bossId) {
      bossEntityId = z.id;
      emit({ type: "bossSpawned", id: z.id, bossId: z.bossId });
    }
    return z;
  }

  function kill(z: ZombieEntity): void {
    if (z === baseEnt) return; // база не «убийство»: победу фиксирует конец тика
    kills++;
    corpses.push({ state: zombieState(z, true), frame });
    emit({ type: "zombieKilled", id: z.id, kind: z.kind });
    if (z.kind === "boss" && z.bossId) {
      bossKilled = true;
      emit({ type: "bossKilled", id: z.id, bossId: z.bossId });
    }
    // в итог идут сырые монеты, `coinGain` применяет economy.waveReward один раз к сумме (в finish);
    // на летящей монете — значение с множителем, только для показа
    if (atk) return; // в бою атаки награду даёт участок, а не убийства
    coinsEarned += z.coins;
    const coins = mulPermille(z.coins, glob.coinGain);
    if (coins > 0) {
      const id = nextVisualId++;
      coinDrops.push({ id, pos: { x: z.x, y: z.y }, value: coins, startTick: frame, flightTicks: COIN_FLIGHT_TICKS });
      emit({ type: "coinsDropped", coinId: id, value: coins });
      effect("coinBurst", z, null, 12, coins);
    }
  }

  function damage(z: ZombieEntity, amount: number, crit: boolean): void {
    if (z.hp <= 0 || amount <= 0) return;
    let rest = amount;
    if (z.shield > 0) {
      const absorbed = Math.min(z.shield, rest);
      z.shield -= absorbed;
      rest -= absorbed;
    }
    z.hp = Math.max(0, z.hp - rest);
    emit({ type: "hit", targetId: z.id, damage: amount, crit });
    effect(crit ? "critNumber" : "damageNumber", z, null, 20, amount);
    if (z.hp === 0) kill(z);
  }

  function zombiesInRadius(x: number, y: number, r: number): ZombieEntity[] {
    const r2 = r * r;
    return zombies.filter((z) => alive(z) && dist2(x, y, z.x, z.y) <= r2);
  }

  /**
   * Бой атаки: сначала защитник, подошедший к платформе (в {@link ATTACK_THREAT_RANGE}), иначе база, если в радиусе,
   * иначе ближайший к стрелку зомби.
   */
  function findTargetAttack(x: number, y: number, range: number): ZombieEntity | null {
    const r2 = range * range;
    const t2 = ATTACK_THREAT_RANGE * ATTACK_THREAT_RANGE;
    let threat: ZombieEntity | null = null;
    let td = 0;
    let near: ZombieEntity | null = null;
    let nd = 0;
    let baseIn = false;
    for (const z of zombies) {
      if (!alive(z)) continue;
      const d = dist2(x, y, z.x, z.y);
      if (d > r2) continue;
      if (z === baseEnt) {
        baseIn = true;
        continue;
      }
      if (d <= t2 && (threat === null || d < td)) {
        threat = z;
        td = d;
      }
      if (near === null || d < nd) {
        near = z;
        nd = d;
      }
    }
    return threat ?? (baseIn ? baseEnt : near);
  }

  function findTarget(x: number, y: number, range: number): ZombieEntity | null {
    if (atk) return findTargetAttack(x, y, range);
    const r2 = range * range;
    let best: ZombieEntity | null = null;
    let bestD = 0;
    for (const z of zombies) {
      if (!alive(z) || dist2(x, y, z.x, z.y) > r2) continue;
      const d = hqDist2(z);
      if (best === null || d < bestD) {
        best = z;
        bestD = d;
      }
    }
    return best;
  }

  function impact(p: {
    owner: HeroId | "hq";
    x: number;
    y: number;
    targetId: number;
    damage: number;
    crit: boolean;
    splash: number;
  }): void {
    const hero = p.owner === "hq" ? undefined : heroes.find((h) => h.heroId === p.owner);
    const onHit = hero ? behaviors[hero.heroId]?.onHit : undefined;
    if (p.splash > 0) {
      for (const z of zombiesInRadius(p.x, p.y, p.splash)) {
        damage(z, p.damage, p.crit);
        if (onHit && alive(z)) onHit(hero!, z, api);
      }
      effect("explosion", p, null, 15, p.splash);
      return;
    }
    const z = zombies.find((q) => q.id === p.targetId);
    if (z && alive(z)) {
      damage(z, p.damage, p.crit);
      if (onHit && alive(z)) onHit(hero!, z, api);
      effect("hit", p, null, 8, 0);
    }
  }

  function fire(s: Shooter, target: ZombieEntity): void {
    const a = s.attack;
    const crit = a.critChance > 0 && rng.chance(a.critChance);
    const dmg = Math.max(1, crit ? mulPermille(a.damage, a.critDamage) : a.damage);
    const id = nextId++;
    s.cooldownLeft = a.cooldownTicks;
    s.targetId = target.id;
    s.facing = facingOf(target.x - s.x, target.y - s.y);
    s.firedFrame = frame;
    emit({ type: "shot", owner: s.owner, projectileId: id });
    effect("muzzle", s, null, 4, 0);
    if (a.projectileSpeed === 0) {
      effect("lightning", s, { x: target.x, y: target.y }, 8, 0);
      impact({
        owner: s.owner,
        x: target.x,
        y: target.y,
        targetId: target.id,
        damage: dmg,
        crit,
        splash: a.splashRadius,
      });
      return;
    }
    projectiles.push({
      id,
      kind: a.projectile,
      owner: s.owner,
      x: s.x,
      y: s.y,
      fromX: s.x,
      fromY: s.y,
      tx: target.x,
      ty: target.y,
      targetId: target.id,
      speed: a.projectileSpeed,
      damage: dmg,
      crit,
      splash: a.splashRadius,
    });
  }

  const api: SimApi = {
    balance,
    rng,
    get tick() {
      return t;
    },
    get frame() {
      return frame;
    },
    hq: layout.hq,
    modifiers: mods,
    zombies: () => zombies.filter(alive),
    findTarget,
    zombiesInRadius,
    fire,
    damage,
    effect,
    emit,
  };

  for (const h of heroes) behaviors[h.heroId]?.init?.(h, api);

  function castSkill(hero: HeroEntity, auto: boolean): void {
    const b = behaviors[hero.heroId];
    if (!b?.castSkill || phase !== "fight" || hero.skill.cooldownLeft > 0) return;
    if (!b.castSkill(hero, api, auto)) return;
    hero.skill.cooldownLeft = hero.skill.cooldownTotal;
    hero.skill.activeLeft = hero.skill.durationTicks;
    hero.anim = "skill";
    hero.animFrame = frame;
    emit({ type: "skillCast", heroId: hero.heroId, skillId: hero.skill.skillId, auto });
  }

  /** Посылка, которую можно открыть на тике `t`. */
  function liveParcel(): (typeof parcels)[number] | null {
    if (phase !== "fight") return null;
    const p = parcels.find((q) => q.tick <= t && t <= q.endTick);
    return p && !parcelsOpened.has(p.id) ? p : null;
  }

  function openParcel(id: number): void {
    const p = liveParcel();
    if (!p || p.id !== id) return;
    parcelsOpened.add(id);
    const r = p.reward;
    if (r.type === "coins") {
      const raw = Math.max(r.minCoins, mulPermille(waveBaseReward(balance, wave), r.waveRewardPermille));
      coinsEarned += raw;
      const coins = mulPermille(raw, glob.coinGain);
      walletCollected += coins;
      lastOpened = { id, reward: "coins", coins, boostId: null };
      emit({ type: "parcelOpened", ...lastOpened });
      return;
    }
    // только здесь `mods` меняется посреди волны: урон и темп пересчитываются, перезарядка идёт как шла
    mods.push(...(balance.parcels?.boosts.find((b) => b.id === r.boostId)?.modifiers ?? []));
    for (const h of heroes) h.attack = heroAttack(balance, h.heroId, h.level, h.stars, mods) ?? h.attack;
    lastOpened = { id, reward: "boost", coins: 0, boostId: r.boostId };
    emit({ type: "parcelOpened", ...lastOpened });
  }

  function apply(cmd: PlayerCommand): void {
    switch (cmd.type) {
      case "castSkill": {
        // копии героя (бета) жмутся вместе: одна кнопка на тип
        for (const h of heroes) if (h.heroId === cmd.heroId) castSkill(h, false);
        break;
      }
      case "setAutoSkills":
        if (autoUnlocked) autoEnabled = cmd.enabled;
        break;
      case "chooseBoost":
        if (phase === "cleared" && boost === null) boost = { index: cmd.index, boostId: cmd.boostId };
        break;
      case "tapParcel":
        openParcel(cmd.parcelId);
        break;
    }
  }

  function finish(outcome: "cleared" | "lost"): void {
    phase = outcome;
    if (outcome === "cleared") emit({ type: "waveCleared", wave });
    else emit({ type: "waveLost", wave, rollbackTo: atk ? wave : rollbackWave(balance, wave) });
    const reward = atk ? { total: 0 } : waveReward(balance, { wave, outcome, killCoins: coinsEarned, modifiers: mods });
    result = {
      outcome,
      ticks: t + 1,
      kills,
      coins: reward.total,
      hqHp: baseEnt ? baseEnt.hp : hqHp,
      bossKilled,
      stateHash: "",
    };
    coinsEarned = reward.total;
    result.stateHash = hash(t + 1);
    // команды после конца волны (выбор усиления: тик ≥ result.ticks) — сразу, в порядке журнала
    while (pending.length > 0) apply(pending.shift()!.command);
  }

  function damageHq(amount: number): void {
    if (amount <= 0 || hqHp <= 0) return;
    hqHp = Math.max(0, hqHp - amount);
    emit({ type: "hqDamaged", damage: amount, hp: hqHp });
    effect("hqHit", layout.hq, null, 10, amount);
  }

  function bossAbility(z: ZombieEntity): void {
    const ab = z.ability;
    if (!ab) return;
    if (ab.chargeLeft > 0) ab.chargeLeft--;
    if (ab.left > 0) ab.left--;
    if (ab.left > 0) return;
    ab.left = ab.cooldownTicks;
    const p = ab.params;
    switch (ab.kind) {
      case "charge":
        if (z.mode === "walk") ab.chargeLeft = p.durationTicks ?? 45;
        break;
      case "summon": {
        const n = p.count ?? 3;
        const spread = p.spread ?? 600;
        for (let i = 0; i < n; i++) {
          const x = z.x + rng.int(-spread, spread);
          const y = z.y + rng.int(-spread, spread);
          const half = divTrunc(layout.field.width, 2);
          if (
            spawnZombie(
              "walker",
              null,
              Math.max(-half, Math.min(half, x)),
              Math.max(0, Math.min(layout.field.depth, y)),
            )
          )
            total++;
        }
        break;
      }
      case "shieldAura": {
        const amount = scaledHp(p.shield ?? 20, plan.hpPermille);
        const cap = p.maxShield !== undefined ? scaledHp(p.maxShield, plan.hpPermille) : amount * 3;
        for (const q of zombiesInRadius(z.x, z.y, p.radius ?? 2500)) {
          if (q !== z) q.shield = Math.max(q.shield, Math.min(cap, q.shield + amount));
        }
        break;
      }
    }
  }

  function moveZombie(z: ZombieEntity): void {
    const dx = layout.hq.x - z.x;
    const dy = layout.hq.y - z.y;
    if (z.mode === "attack") {
      if (z.speedPermille <= 0) return; // заморожен — не бьёт
      z.attackLeft--;
      if (z.attackLeft <= 0) {
        z.attackLeft = z.attackCooldownTicks;
        damageHq(z.damage);
      }
      return;
    }
    let speed = mulPermille(mulPermille(z.speed, z.speedPermille), glob.zombieSpeed);
    if (z.ability && z.ability.chargeLeft > 0) speed = mulPermille(speed, z.ability.params.speedPermille ?? 3000);
    const d = isqrt(dx * dx + dy * dy);
    const stop = HQ_RADIUS + z.radius;
    z.facing = facingOf(dx, dy);
    if (d - stop <= speed) {
      if (d > stop) {
        z.x = layout.hq.x - divTrunc(dx * stop, d);
        z.y = layout.hq.y - divTrunc(dy * stop, d);
      }
      z.mode = "attack";
      z.animFrame = frame;
      z.attackLeft = z.attackCooldownTicks;
      return;
    }
    if (speed <= 0) return;
    z.x += divTrunc(dx * speed, d);
    z.y += divTrunc(dy * speed, d);
  }

  /** Бой атаки: урон платформе атакующего; разрушенная убирает героя. */
  function damagePlatform(slot: number, amount: number): void {
    const left = platformHp.get(slot) ?? 0;
    if (amount <= 0 || left <= 0) return;
    const hp = Math.max(0, left - amount);
    platformHp.set(slot, hp);
    emit({ type: "platformDamaged", slot, damage: amount, hp });
    const pos = layout.platforms[slot]!;
    effect("hqHit", pos, null, 10, amount);
    if (hp > 0) return;
    const i = heroes.findIndex((h) => h.slot === slot);
    if (i >= 0) heroes.splice(i, 1);
    emit({ type: "platformDestroyed", slot });
    for (const [id, s] of [...zombieTarget]) if (s === slot) zombieTarget.delete(id);
  }

  /** Бой атаки: защитник идёт к ближайшей платформе с героем и бьёт её. */
  function moveAttackZombie(z: ZombieEntity): void {
    if (z.mode === "attack") {
      const slot = zombieTarget.get(z.id);
      if (slot === undefined || !heroes.some((h) => h.slot === slot)) {
        z.mode = "walk";
        z.animFrame = frame;
        zombieTarget.delete(z.id);
      } else {
        if (z.speedPermille <= 0) return;
        z.attackLeft--;
        if (z.attackLeft <= 0) {
          z.attackLeft = z.attackCooldownTicks;
          damagePlatform(slot, z.damage);
        }
        return;
      }
    }
    let best: HeroEntity | null = null;
    let bd = 0;
    for (const h of heroes) {
      const d = dist2(z.x, z.y, h.x, h.y);
      if (best === null || d < bd) {
        best = h;
        bd = d;
      }
    }
    if (!best) return;
    const dx = best.x - z.x;
    const dy = best.y - z.y;
    const speed = mulPermille(mulPermille(z.speed, z.speedPermille), glob.zombieSpeed);
    const d = isqrt(dx * dx + dy * dy);
    const stop = PLATFORM_RADIUS + z.radius;
    z.facing = facingOf(dx, dy);
    if (d - stop <= speed) {
      if (d > stop) {
        z.x = best.x - divTrunc(dx * stop, d);
        z.y = best.y - divTrunc(dy * stop, d);
      }
      z.mode = "attack";
      z.animFrame = frame;
      z.attackLeft = z.attackCooldownTicks;
      zombieTarget.set(z.id, best.slot);
      return;
    }
    if (speed <= 0) return;
    z.x += divTrunc(dx * speed, d);
    z.y += divTrunc(dy * speed, d);
  }

  /** Бой атаки: герои-защитники бьют ближайшую платформу атакующего в радиусе (мгновенное попадание). */
  function defenderTick(dh: Defender): void {
    if (dh.cooldownLeft > 0) dh.cooldownLeft--;
    if (dh.anim === "shoot" && frame - dh.animFrame >= SHOOT_ANIM_TICKS) {
      dh.anim = "idle";
      dh.animFrame = frame;
    }
    if (dh.cooldownLeft > 0) return;
    let best: HeroEntity | null = null;
    let bd = 0;
    const r2 = dh.attack.range * dh.attack.range;
    for (const h of heroes) {
      const d = dist2(dh.x, dh.y, h.x, h.y);
      if (d <= r2 && (best === null || d < bd)) {
        best = h;
        bd = d;
      }
    }
    if (!best) {
      dh.targetSlot = null;
      return;
    }
    const a = dh.attack;
    const crit = a.critChance > 0 && rng.chance(a.critChance);
    const raw = crit ? mulPermille(a.damage, a.critDamage) : a.damage;
    const dmg = Math.max(1, mulPermille(raw, amap!.defenderDamagePermille));
    dh.cooldownLeft = a.cooldownTicks;
    dh.targetSlot = best.slot;
    dh.facing = facingOf(best.x - dh.x, best.y - dh.y);
    dh.firedFrame = frame;
    dh.anim = "shoot";
    dh.animFrame = frame;
    emit({ type: "shot", owner: dh.heroId, projectileId: nextId++ });
    effect("muzzle", dh, null, 4, 0);
    effect("hit", best, null, 8, 0);
    damagePlatform(best.slot, dmg);
  }

  function moveProjectiles(): void {
    for (const p of projectiles) {
      const target = zombies.find((z) => z.id === p.targetId);
      if (target && alive(target)) {
        p.tx = target.x;
        p.ty = target.y;
      }
      const dx = p.tx - p.x;
      const dy = p.ty - p.y;
      const d = isqrt(dx * dx + dy * dy);
      if (d <= p.speed) {
        p.x = p.tx;
        p.y = p.ty;
        p.speed = -1; // попал
        impact(p);
      } else {
        p.x += divTrunc(dx * p.speed, d);
        p.y += divTrunc(dy * p.speed, d);
      }
    }
    projectiles = projectiles.filter((p) => p.speed >= 0);
  }

  function heroTick(h: HeroEntity): void {
    const b = behaviors[h.heroId];
    const sk = h.skill;
    if (sk.activeLeft > 0) sk.activeLeft--;
    if (sk.cooldownLeft > 0) {
      sk.cooldownLeft--;
      if (sk.cooldownLeft === 0) emit({ type: "skillReady", heroId: h.heroId });
    }
    if (autoEnabled && sk.cooldownLeft === 0) castSkill(h, true);
    b?.tick(h, api);
    const shot = h.firedFrame === frame;
    if (shot) {
      h.anim = "shoot";
      h.animFrame = frame;
    } else if (h.anim === "shoot" && frame - h.animFrame >= SHOOT_ANIM_TICKS) {
      h.anim = "idle";
      h.animFrame = frame;
    } else if (h.anim === "skill" && sk.activeLeft === 0 && frame - h.animFrame >= SHOOT_ANIM_TICKS) {
      h.anim = "idle";
      h.animFrame = frame;
    }
  }

  function simulateTick(): void {
    while (pending.length > 0 && pending[0]!.tick <= t) apply(pending.shift()!.command);

    if (t === 0) {
      emit({ type: "waveStarted", wave, isBoss: plan.isBoss });
      for (const h of heroes) {
        if (balance.heroes.find((d) => d.id === h.heroId)?.unlockWave === wave) {
          emit({ type: "heroUnlocked", heroId: h.heroId, slot: h.slot });
        }
      }
    }
    if (phase === "intro" && t >= plan.introTicks) phase = "fight";
    if (phase === "intro") return;

    // снимок этого шага уже смотрит на тик `t + 1`: посылка и событие появляются вместе
    for (const p of parcels) if (p.tick === t + 1) emit({ type: "parcelDropped", id: p.id });

    while (spawnIdx < plan.spawns.length && plan.spawns[spawnIdx]!.tick <= t) {
      const s: SpawnEntry = plan.spawns[spawnIdx++]!;
      if (!spawnZombie(s.kind, s.bossId, s.x, s.y)) total--;
    }

    for (const z of zombies) if (alive(z) && z.ability) bossAbility(z);
    if (atk) {
      for (const z of zombies) if (alive(z) && z !== baseEnt) moveAttackZombie(z);
      for (const dh of defenders) defenderTick(dh);
      if (heroes.length === 0 || t - plan.introTicks >= amap!.timeLimitTicks) {
        finish("lost");
        return;
      }
      for (const h of heroes) heroTick(h);
      moveProjectiles();
      zombies = zombies.filter(alive);
      if (baseEnt!.hp <= 0) finish("cleared");
      return;
    }
    for (const z of zombies) if (alive(z)) moveZombie(z);

    if (hqHp <= 0 && t + 1 < MAX_WAVE_TICKS) {
      const back = init.hooks?.onHqDown?.(t) ?? 0;
      if (back > 0) hqHp = mulPermille(hqs.hpMax, back);
    }
    if (hqHp <= 0 || t + 1 >= MAX_WAVE_TICKS) {
      finish("lost");
      return;
    }
    if (hqs.regenPerTick > 0) hqHp = Math.min(hqs.hpMax, hqHp + hqs.regenPerTick);

    for (const h of heroes) heroTick(h);
    shooterTick(cannon, api);
    moveProjectiles();

    zombies = zombies.filter(alive);
    if (spawnIdx >= plan.spawns.length && zombies.length === 0) finish("cleared");
  }

  function visualTick(): void {
    effects = effects.filter((e) => frame - e.startTick < e.durationTicks);
    const landed = coinDrops.filter((c) => frame - c.startTick >= c.flightTicks);
    if (landed.length > 0) {
      coinDrops = coinDrops.filter((c) => frame - c.startTick < c.flightTicks);
      for (const c of landed) {
        walletCollected += c.value;
        emit({ type: "coinsCollected", value: c.value });
      }
    }
    corpses = corpses.filter((c) => frame - c.frame < DIE_TICKS);
  }

  function hash(ticks: number): string {
    const h = new StateHasher();
    h.int(ticks).int(rng.state()).int(PHASE_CODE[phase]).int(hqHp).int(nextId).int(spawnIdx).int(total);
    h.int(spawned).int(kills).int(coinsEarned).bool(bossKilled).bool(autoEnabled);
    if (parcelsOpened.size > 0) h.int(parcelsOpened.size); // без тапа хэш как до посылок: журнал не зависит от раздела
    h.int(cannon.cooldownLeft).int(cannon.targetId ?? -1);
    for (const x of heroes) {
      h.int(x.id)
        .str(x.heroId)
        .int(x.cooldownLeft)
        .int(x.targetId ?? -1);
      h.int(x.skill.cooldownLeft).int(x.skill.activeLeft);
      for (const k of Object.keys(x.data).sort()) h.str(k).int(x.data[k]!);
    }
    h.int(zombies.length);
    for (const z of zombies) {
      h.int(z.id).int(ZOMBIE_KIND_CODE[z.kind]).int(z.x).int(z.y).int(z.hp).int(z.shield);
      h.int(z.speedPermille)
        .int(z.attackLeft)
        .bool(z.mode === "attack");
      if (z.ability) h.int(z.ability.left).int(z.ability.chargeLeft);
      for (const k of Object.keys(z.data).sort()) h.str(k).int(z.data[k]!);
    }
    h.int(projectiles.length);
    for (const p of projectiles) h.int(p.id).int(p.x).int(p.y).int(p.targetId).int(p.damage);
    if (atk) {
      // только бой атаки: обычные хэши не меняются
      for (const [slot, hp] of [...platformHp].sort((a, b) => a[0] - b[0])) h.int(slot).int(hp);
      for (const d of defenders)
        h.int(d.id)
          .int(d.cooldownLeft)
          .int(d.targetSlot ?? -1);
    }
    return h.hex();
  }

  function hqLook(): HqLook {
    const hp = baseEnt ? baseEnt.hp : hqHp;
    const max = baseEnt ? baseEnt.hpMax : hqs.hpMax;
    if (hp <= 0) return "destroyed";
    if (hp * 3 > max * 2) return "intact";
    if (hp * 3 > max) return "damaged";
    return "critical";
  }

  function attackState(): AttackState | undefined {
    if (!atk || !amap) return undefined;
    return {
      kind: atk.kind,
      ticksLeft: Math.max(0, amap.timeLimitTicks - Math.max(0, t - plan.introTicks)),
      ticksTotal: amap.timeLimitTicks,
      platformHp: [...platformHp].sort((a, b) => a[0] - b[0]).map(([slot, hp]) => ({ slot, hp, hpMax: platformHpMax })),
      defenders: defenders.map((d) => ({
        id: d.id,
        heroId: d.heroId,
        slot: d.slot,
        pos: { x: d.x, y: d.y },
        facing: d.facing,
        anim: d.anim,
        animTick: Math.max(0, frame - d.animFrame),
        level: d.level,
        stars: d.stars,
        targetSlot: d.targetSlot,
      })),
    };
  }

  return {
    plan,
    step() {
      events = [];
      if (result === null) {
        simulateTick();
        t++;
      }
      visualTick();
      frame++;
      return events;
    },
    snapshot(): BattleSnapshot {
      const boss = bossEntityId === null ? undefined : zombies.find((z) => z.id === bossEntityId && alive(z));
      return {
        frame,
        field: { ...layout.field },
        wave: {
          ...(level ? { level } : {}),
          number: wave,
          isBoss: plan.isBoss,
          phase,
          tick: t,
          total,
          spawned,
          killed: kills,
          progress: total > 0 ? Math.min(PERMILLE, divTrunc(kills * PERMILLE, total)) : 0,
          boss: boss ? { entityId: boss.id, bossId: boss.bossId!, hp: boss.hp, hpMax: boss.hpMax } : null,
        },
        hq: {
          pos: { ...layout.hq },
          hp: baseEnt ? baseEnt.hp : hqHp,
          hpMax: baseEnt ? baseEnt.hpMax : hqs.hpMax,
          look: hqLook(),
          cannon: {
            facing: cannon.facing,
            firing: cannon.firedFrame === frame - 1,
            targetId: cannon.targetId,
          },
        },
        platforms: layout.platforms.map((pos, slot) => ({
          slot,
          pos: { ...pos },
          unlocked: platformUnlock[slot]! <= openedWave,
          unlockWave: platformUnlock[slot]!,
          heroId: heroes.find((h) => h.slot === slot)?.heroId ?? null,
        })),
        heroes: heroes.map((h) => ({
          id: h.id,
          heroId: h.heroId,
          slot: h.slot,
          pos: { x: h.x, y: h.y },
          facing: h.facing,
          anim: h.anim,
          animTick: Math.max(0, frame - h.animFrame),
          level: h.level,
          stars: h.stars,
          targetId: h.targetId,
          skill: {
            skillId: h.skill.skillId,
            cooldownLeft: h.skill.cooldownLeft,
            cooldownTotal: h.skill.cooldownTotal,
            activeLeft: h.skill.activeLeft,
          },
        })),
        zombies: [
          ...zombies.filter((z) => alive(z) && z !== baseEnt).map((z) => zombieState(z, false)),
          ...corpses.map((c) => ({ ...c.state, animTick: frame - c.frame })),
        ],
        projectiles: projectiles.map((p) => ({
          id: p.id,
          kind: p.kind,
          pos: { x: p.x, y: p.y },
          from: { x: p.fromX, y: p.fromY },
          to: { x: p.tx, y: p.ty },
          owner: p.owner,
        })),
        effects: effects.map((e) => ({ ...e, pos: { ...e.pos }, to: e.to ? { ...e.to } : null })),
        coins: coinDrops.map((c) => ({ ...c, pos: { ...c.pos } })),
        parcels: liveParcel()
          ? [liveParcel()!].map((p) => ({ id: p.id, spot: { ...p.spot }, startTick: p.tick, endTick: p.endTick }))
          : [],
        parcelOpened: lastOpened,
        wallet: { coins: (init.walletCoins ?? 0) + walletCollected, earnedThisWave: walletCollected },
        autoSkills: { unlocked: autoUnlocked, enabled: autoEnabled },
        events: [...events],
        ...(atk ? { attack: attackState()! } : {}),
      };
    },
    command(cmd) {
      const tc: TimedCommand = { tick: t, command: cmd };
      log.push(tc);
      if (result !== null) apply(cmd);
      else pending.push(tc);
    },
    schedule(tc) {
      log.push(tc);
      if (result !== null && tc.tick >= t) apply(tc.command);
      else {
        let i = pending.length;
        while (i > 0 && pending[i - 1]!.tick > tc.tick) i--;
        pending.splice(i, 0, tc);
      }
    },
    commands: () => log.map((c) => ({ tick: c.tick, command: { ...c.command } })),
    allowAutoSkills() {
      autoUnlocked = autoFromWave;
    },
    autoSkillsAllowed: () => autoUnlocked,
    tick: () => t,
    isOver: () => result !== null,
    result: () => (result ? { ...result } : null),
    stateHash: () => hash(t),
    boostChoice: () => (boost ? { ...boost } : null),
  };
}
